import {
  Ach,
  AdyenCheckout,
  ApplePay,
  Card,
  GooglePay,
} from "@adyen/adyen-web";
import "@adyen/adyen-web/styles/adyen.css";
import "@adyen/kyc-components/styles.css";
import { RefObject, useEffect, useRef, useState } from "react";
import { PaymentSessionQuery } from "src/api/Payment";
import { UpsertStripeCustomerMutation } from "src/api/Stripe";
import { CreditCardType } from "src/components/forms/CreditCardIcon";
import Loading from "src/components/layout/Loading";
import { apiClient } from "src/stores/Api";
import { useSession } from "src/stores/Session";
import { useStore } from "src/stores/Store";
import "src/styles/adyen.css";
import colors from "src/utils/colors";
import formatMoney from "src/utils/formatMoney";
import tw from "src/utils/tw";
import {
  PaymentSessionType,
  upsertStripeCustomer,
  upsertStripeCustomerVariables,
} from "types/code-generator";

interface Props {
  user: {
    id: string;
  };
  adyenContainer: RefObject<any>;
  onReceiveToken: (payload: {
    adyen: upsertStripeCustomer["upsertStripeCustomer"];
  }) => void;
  ach?: boolean;
  onError: (error: any) => void;
  totalOwed?: number;
  omitGooglePay?: boolean;
  omitApplePay?: boolean;
}

export function AdyenPaymentForm(props: Props) {
  const store = useStore();
  const session = useSession();
  const container = useRef(null);
  const googlePayContainer = useRef(null);
  const applePayContainer = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [googlePayMounted, setGooglePayMounted] = useState(false);
  const [applePayMounted, setApplePayMounted] = useState(false);

  useEffect(() => {
    setupCheckout();
  }, []);

  async function setupCheckout() {
    const paymentSessionQuery = await apiClient(PaymentSessionQuery, {
      variables: {
        data: {
          type: PaymentSessionType.Checkout,
        },
      },
    });
    const paymentSession = paymentSessionQuery.paymentSession;

    const checkout = await AdyenCheckout({
      session: {
        id: paymentSession.id,
        sessionData: paymentSession.sessionData,
      },
      environment:
        process.env.NEXT_PUBLIC_STAGE === "prod" ? "live-us" : "test",
      clientKey: process.env.NEXT_PUBLIC_ADYEN_CLIENT,
      onPaymentCompleted: (response, _component) => {
        console.log("response", response);
      },
      onError: (error, _component) => {
        console.log("AdyenCheckout e", error);

        if (props.onError) {
          props.onError(error);
        }
      },
      onSubmit: async (state, component, actions) => {
        console.log("state", state, props.user);
        const accountOwner = props.user || session.accountOwner;

        if (state.isValid) {
          const paymentMethod = state.data.paymentMethod;
          let brand = paymentMethod.brand;
          let encryptedCardNumber = paymentMethod.encryptedCardNumber;

          if (paymentMethod.type === "googlepay") {
            brand = CreditCardType["Google Pay"];
            encryptedCardNumber = paymentMethod.googlePayToken;
          }

          if (paymentMethod.type === "applepay") {
            brand = CreditCardType["Apple Pay"];
            encryptedCardNumber = paymentMethod.applePayToken;
          }

          if (!brand) {
            brand = CreditCardType["Bank Account"];
          }

          const variables: upsertStripeCustomerVariables = {
            data: {
              userId: accountOwner.id,
              organizationId: store.organization.id,
              adyenToken: {
                brand,
                encryptedCardNumber,
                encryptedExpiryMonth: paymentMethod.encryptedExpiryMonth,
                encryptedExpiryYear: paymentMethod.encryptedExpiryYear,
                encryptedSecurityCode: paymentMethod.encryptedSecurityCode,
                accountHolderType: paymentMethod.accountHolderType,
                bankAccountNumber: paymentMethod.bankAccountNumber,
                bankAccountType: paymentMethod.bankAccountType,
                bankLocationId: paymentMethod.bankLocationId,
                ownerName: paymentMethod.ownerName,
                checkoutAttemptId: paymentMethod.checkoutAttemptId,
              },
            },
          };

          try {
            const upsertAdyenCustomer = await apiClient(
              UpsertStripeCustomerMutation,
              {
                variables,
              }
            );

            console.log("upsertAdyenCustomer", upsertAdyenCustomer);

            props.onReceiveToken({
              adyen: upsertAdyenCustomer.upsertStripeCustomer,
            });

            actions.resolve({
              resultCode: "Authorised",
            });
          } catch (e) {
            console.log("e", e);

            actions.reject({
              error: e,
            });

            if (props.onError) {
              props.onError(e);
            }
          }
        }
      },
    });

    if (props.ach) {
      if (container.current) {
        const achElement = new Ach(checkout, {
          showPayButton: false,
        }).mount(container.current);

        setMounted(true);

        if (props.adyenContainer) {
          props.adyenContainer.current = achElement;
        }
      }
    } else {
      if (container.current) {
        const card = new Card(checkout, {
          styles: {
            base: {
              fontFamily: '"soleil", sans-serif',
              color: colors.dark,
            },
          },
          showPayButton: false,
          enableStoreDetails: true,
        }).mount(container.current);

        setMounted(true);

        if (props.adyenContainer) {
          props.adyenContainer.current = card;
        }
      }

      if (
        !props.omitGooglePay &&
        !props.omitApplePay &&
        ["captyn-adyen", "nitroswim"].includes(store.organization.subdomain)
      ) {
        const googlePayComponent = new GooglePay(checkout, {
          onClick(resolve, reject) {
            resolve();
          },
          onAuthorized(data, actions) {
            console.log("onAuthorized data", data);
            actions.resolve();
          },
          transactionInfo: {
            totalPrice: formatMoney(props.totalOwed || 0),
            totalPriceStatus: "FINAL",
            countryCode: "US",
            currencyCode: "USD",
          },
          configuration: {
            merchantName: store.organization.title,
            merchantId: paymentSession.googlePay?.merchantId,
            gatewayMerchantId: paymentSession.googlePay?.gatewayMerchantId,
          },
        });

        googlePayComponent
          .isAvailable()
          .then(() => {
            if (!googlePayMounted) {
              setGooglePayMounted(true);
              googlePayComponent.mount(googlePayContainer.current);
            }
          })
          .catch((e) => {
            console.log("googlePay e", e);
          });

        const applePayComponent = new ApplePay(checkout, {
          configuration: {
            merchantName: paymentSession.applePay?.merchantName,
            merchantId: paymentSession.applePay?.merchantId,
          },
          // onValidateMerchant: (resolve, reject, validationUrl) => {
          //   console.log("onValidateMerchant", validationUrl);
          //   resolve();
          // },
          onClick(resolve, reject) {
            resolve();
          },
          onAuthorized: (data, actions) => {
            console.log("onAuthorized data", data);
            actions.resolve();
          },
          totalPriceLabel: `$${formatMoney(props.totalOwed || 0)}`,
          totalPriceStatus: "final",
          amount: {
            currency: "USD",
            value: props.totalOwed || 0,
          },
          // recurringPaymentRequest: {
          //   paymentDescription: `Store Payment Details`,
          //   managementURL: `https://${store.organization.subdomain}.captyn.com/account`,
          //   tokenNotificationURL: "https://www.adyen.com/tokenNotificationUrl",
          //   regularBilling: {
          //     label: `${store.organization.title}`,
          //     amount: formatMoney(props.totalOwed || 0),
          //     type: "final",
          //     paymentTiming: "recurring",
          //     recurringPaymentStartDate: new Date(),
          //   },
          // },
        });

        applePayComponent
          .isAvailable()
          .then(() => {
            setApplePayMounted(true);
            applePayComponent.mount(applePayContainer.current);
          })
          .catch((e) => {
            console.log("applePay e", e);
          });
      }
    }
  }

  return (
    <div className="max-w-[770px]">
      <div className={tw(mounted && "hidden")}>
        <Loading />
      </div>

      <div ref={applePayContainer} className="mb-4"></div>
      <div ref={googlePayContainer} className="mb-4"></div>

      {(googlePayMounted || applePayMounted) && (
        <>
          <div className="-mb-4 mt-8 text-center">
            <hr />
            <div className="relative -top-8 mx-auto inline-block w-8 bg-white text-center text-empty">
              or
            </div>
          </div>
        </>
      )}

      <div ref={container} className="payment"></div>
    </div>
  );
}
